import { useEffect, useRef } from "react";
import { Alert } from "../../components/Alert";
import { CurrentSellerRates, FullSellerRatesHistory, SellerRatesHistoryPaginatorDataContent } from "../../models/Rates";
import { Container } from "./styles";
import { BankSlipSection } from "./_BankSlipSection";
import { DebitSection } from "./_DebitSection";
import { EarlyCreditCardSection } from "./_EarlyCreditCardSection";
import { ObservationSection } from "./_ObservationSection";
import { PixSection } from "./_PixSection";
import { SightCreditCardSection } from "./_SightCreditCardSection";
import { TransactionalCostsModal } from "./_TransactionalCostsModal";
import { UpdateHistorySection } from "./_UpdateHistorySection";

export interface TaxSectionProps {
  editionMode?: boolean;
  asModalView?: boolean;
  register?: any;
  errors?: any;
  watch?: any;
  setValue?: any;
  control?: any;
  colorFinder?: any;
}

interface TransactionalCostsProps {
  currentSellerRates: CurrentSellerRates;
  defaultSellerRates: CurrentSellerRates;
  colorFinder: any;
  returnDefaultValue: any;
  hasReturnValue: any;
  setCurrentSellerRates: any;
  onSubmit: any;
  handleSubmit: any;
  sellerPaymentReceiptType?:string;
  register: any;
  handleValue: any;
  watch: any;
  errors: any;
  setValue: any;
  control: any;
  setValuesToDefault: () => void;
  undoChanges: () => void;
  confirmationModalState: boolean;
  setConfirmationModalState: (param: boolean) => void;
  editionModeState: boolean;
  setEditionModeState: (param: boolean) => void;
  allValuesAreDefault: boolean;
  sellerRatesHistory: SellerRatesHistoryPaginatorDataContent;
  handleGetSellerRatesHistory: () => void;
  allValuesAreEqualToCurrentSellerRates: boolean;
  undoChangesAlertState: boolean;
  setUndoChangesAlerState: (param: boolean) => void;
  defaultAlertState: boolean;
  setDefaultAlertState: (param: boolean) => void;
  allBuyerRatesAreDefault: boolean;
  allSellerRatesAreDefault: boolean;
  handleGetFullSellerRatesHistory: (id: string) => void;
  fullHistoryResponse: FullSellerRatesHistory;
  setTabStatus: (param: string) => void;
  sellerSplitData: any;
}

export function TransactionalCosts({
  sellerSplitData,
  currentSellerRates,
  defaultSellerRates,
  sellerPaymentReceiptType,
  colorFinder,
  returnDefaultValue,
  hasReturnValue,
  setCurrentSellerRates,
  onSubmit,
  handleSubmit,
  register,
  handleValue,
  watch,
  errors,
  setValue,
  control,
  setValuesToDefault,
  undoChanges,
  confirmationModalState,
  setConfirmationModalState,
  editionModeState,
  setEditionModeState,
  allValuesAreDefault,
  sellerRatesHistory,
  handleGetSellerRatesHistory,
  allValuesAreEqualToCurrentSellerRates,
  setDefaultAlertState,
  setUndoChangesAlerState,
  defaultAlertState,
  undoChangesAlertState,
  allSellerRatesAreDefault,
  allBuyerRatesAreDefault,
  handleGetFullSellerRatesHistory,
  fullHistoryResponse,
  setTabStatus
}: TransactionalCostsProps) {
  const ref = useRef();
  function handleUndoChanges() {
    undoChanges();
    setUndoChangesAlerState(!undoChangesAlertState);
    setEditionModeState(!editionModeState);
  }
  function handleSetValuesToDefault() {
    setValuesToDefault();
    setDefaultAlertState(!defaultAlertState);
  }
  const handleTabClose = (event: any) => {
    event.preventDefault();
    return '';
  };
  useEffect(() => {
    setConfirmationModalState(false);
    // eslint-disable-next-line
  }, [errors]);
  useEffect(() => {
    if (editionModeState) {
      window.onbeforeunload = handleTabClose;
    }
    window.onbeforeunload = null;
    // eslint-disable-next-line
  }, [editionModeState]);
  return (
    <>
      {currentSellerRates && (
        <>
          <TransactionalCostsModal
            control={control}
            errors={errors}
            onSubmit={onSubmit}
            handleSubmit={handleSubmit}
            register={register}
            defaultSellerRates={defaultSellerRates}
            modalState={confirmationModalState}
            closeModal={() => setConfirmationModalState(!confirmationModalState)}
            currentSellerRates={currentSellerRates}
            watch={watch}
            colorFinder={colorFinder}
            asHistoryVisualization={!editionModeState}
            fullHistoryVisualization={fullHistoryResponse}
          />
          <Alert
            isOpen={undoChangesAlertState}
            onClose={() => setUndoChangesAlerState(!undoChangesAlertState)}
            title="Tem certeza que deseja descartar as mudanças?"
            messageBody="As alterações que você fez agora serão perdidas."
            confirmButtonText="Descartar mudanças"
            closeButtonText="Cancelar"
            onConfirm={handleUndoChanges}
            leastDestructiveRef={ref}
          />
          <Alert
            isOpen={defaultAlertState}
            onClose={() => setDefaultAlertState(!defaultAlertState)}
            title="Tem certeza que deseja redefinir para os valores padrão?"
            messageBody="Todos os valores serão redefinidos para os valores padrão e essa operação não poderá ser desfeita."
            confirmButtonText="Redefinir"
            closeButtonText="Cancelar"
            bgColor="#FF4D4F"
            onConfirm={handleSetValuesToDefault}
            leastDestructiveRef={ref}
          />
          <Container modalIsOpen={confirmationModalState}>
            <PixSection
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              colorFinder={colorFinder}
              defaultPixConfigData={defaultSellerRates?.pixConfigData}
              pixConfigData={currentSellerRates?.pixConfigData}
              editionMode={editionModeState}
            />
            <BankSlipSection
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              colorFinder={colorFinder}
              defaultBankSlipConfigData={defaultSellerRates?.bankSlipConfigData}
              bankSlipConfigData={currentSellerRates?.bankSlipConfigData}
              editionMode={editionModeState}
            />
            <DebitSection 
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              colorFinder={colorFinder}
              defaultDebitConfigData={defaultSellerRates?.debitCardConfigData}
              debitConfigData={currentSellerRates?.debitCardConfigData}
              editionMode={editionModeState}
            />


            <SightCreditCardSection
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              sellerPaymentReceiptType={sellerPaymentReceiptType}
              colorFinder={colorFinder}
              defaultSightCreditCardConfigData={defaultSellerRates?.sightCreditCardConfigData}
              sightCreditCardConfigData={currentSellerRates?.sightCreditCardConfigData}
              editionMode={editionModeState}
              splitStatus={sellerSplitData.splitStatus === "ACTIVE"}
            />

            <EarlyCreditCardSection
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              control={control}
              colorFinder={colorFinder}
              sellerPaymentReceiptType={sellerPaymentReceiptType}
              defaultEarlyCreditCardConfigData={defaultSellerRates?.earlyCreditCardConfigData}
              earlyCreditCardConfigData={currentSellerRates?.earlyCreditCardConfigData}
              editionMode={editionModeState}
              allBuyerRatesAreDefault={allBuyerRatesAreDefault}
              allSellerRatesAreDefault={allSellerRatesAreDefault}
              splitStatus={sellerSplitData.splitStatus === "ACTIVE" && sellerSplitData.businessModelType === "SPLIT_SUB_ACQUIRER"}
            />
            {editionModeState && (
              <ObservationSection
                register={register}
                errors={errors?.observation}
                watch={watch}
              />
            )}
            <UpdateHistorySection
              fullHistoryResponse={fullHistoryResponse}
              handleGetFullSellerRatesHistory={handleGetFullSellerRatesHistory}
              handleGetSellerRatesHistory={handleGetSellerRatesHistory}
              sellerRatesHistory={sellerRatesHistory}
              editionMode={editionModeState}
              openModal={() => setConfirmationModalState(!confirmationModalState)}
            />
          </Container>
        </>
      )}
    </>
  )
}